/**
 * EPIC CLEANUP — the pure arithmetic behind renaming and deleting an epic.
 *
 * An epic is FREE TEXT on a card (see EpicField), not a stored object, so there is
 * nothing to rename or delete on its own — the name exists only through the cards
 * that carry it. Every cleanup is therefore the SAME move: take every card whose
 * epic is `from` and set its epic to `to`. That one operation covers all four cases
 * the Settings screen offers:
 *
 *   - Rename:            to = the new name
 *   - Rename that merges: to = an existing epic's name (the cards simply join it)
 *   - Delete → reassign:  to = another epic's name
 *   - Delete → clear:     to = '' (the cards become "No epic")
 *
 * This is the pure half (which cards change, and to what), unit-tested on Node; the
 * store applies the changes through updateIssue so each card's history records the
 * change, the same as any other edit.
 */

/** The minimum of a card this needs: its id and its current epic. */
export interface EpicCard {
  id: string;
  epic?: string;
}

export interface EpicChange {
  id: string;
  epic: string;
}

/**
 * The cards that must change to move every card on epic `from` to epic `to`, and
 * what each becomes. Matches the epic string EXACTLY (it is the value the list
 * showed), so two case-variant epics stay distinct until one is deliberately
 * renamed onto the other. A card already on `to`, or on no epic, is left alone.
 */
export function epicReassign(cards: EpicCard[], from: string, to: string): EpicChange[] {
  if (from === '' || from === to) return [];
  return cards.filter((c) => (c.epic ?? '') === from).map((c) => ({ id: c.id, epic: to }));
}

/** How many cards carry an exact epic name — the count the dialogs lead with. */
export function epicCardCount(cards: EpicCard[], name: string): number {
  if (name === '') return 0;
  return cards.reduce((n, c) => n + ((c.epic ?? '') === name ? 1 : 0), 0);
}

/** The distinct epics in use, sorted, for the list and the reassign picker. */
export function epicsInUse(cards: EpicCard[]): string[] {
  return [...new Set(cards.map((c) => c.epic).filter((e): e is string => !!e))].sort((a, b) =>
    a.localeCompare(b)
  );
}

/** True when renaming `from` to `to` would merge into an epic that already exists
 *  (a different name, case and all, that is already in use). Drives the merge
 *  notice and the "Merge epics" button word. */
export function isEpicMerge(cards: EpicCard[], from: string, to: string): boolean {
  const t = to.trim();
  if (t === '' || t === from) return false;
  return cards.some((c) => (c.epic ?? '') === t);
}
